import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Paperclip, X, FolderOpen, UploadSimple, FilePdf, Image as ImageIcon, File as FileIcon, CheckCircle } from "@phosphor-icons/react";

const CAP = 20 * 1024 * 1024;

function fmtSize(n) {
  if (!n) return "";
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}
function Icon({ type = "" }) {
  if (type.includes("pdf")) return <FilePdf size={14} className="text-red-500 shrink-0" />;
  if (type.startsWith("image/")) return <ImageIcon size={14} className="text-sky-500 shrink-0" />;
  return <FileIcon size={14} className="text-muted-foreground shrink-0" />;
}

/** Reply attachments: upload from the device or pick files already on the linked request / job. */
export function ReplyAttachments({ item, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const total = value.reduce((s, a) => s + (a.size || 0), 0);

  useEffect(() => {
    if (!open || files) return;
    API.get(`/inbox/${item.id}/reply-files`).then(({ data }) => setFiles(data)).catch(() => setFiles({ files: [], linked: null }));
  }, [open, files, item.id]);

  const add = (a) => {
    if (value.some(v => (v.file_id && v.file_id === a.file_id) || (v.url && v.url === a.url))) { toast.message("Already attached"); return; }
    if (total + (a.size || 0) > CAP) { toast.error("That would take the reply over the 20 MB limit"); return; }
    onChange([...value, a]);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const f of picked) {
        if (f.size > CAP) { toast.error(`${f.name} is over 20 MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", "inbox-replies");
        const { data } = await API.post("/upload", fd);
        add({ file_id: data.file_id, url: data.file_url, name: f.name, size: data.size || f.size, type: f.type, source: "upload" });
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-1.5" data-testid="reply-attachments">
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={inputRef} type="file" multiple className="hidden" onChange={upload} data-testid="reply-attach-file-input" />
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="reply-attach-upload-btn">
          <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Attach from device"}
        </Button>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline" data-testid="reply-attach-linked-btn"><FolderOpen size={14} className="mr-1" /> Files on the {item.job_request_id ? "request" : item.linked_entity_id ? "job" : "request"}</Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-96 p-1" data-testid="reply-attach-linked-picker">
            {!files ? <p className="text-xs text-muted-foreground px-2 py-2">Loading…</p>
              : !files.linked ? <p className="text-xs text-muted-foreground px-2 py-2">This email isn't linked to a request or job yet — convert or add it first to pick from its files.</p>
              : files.files.length === 0 ? <p className="text-xs text-muted-foreground px-2 py-2">No files on {files.linked.reference || "the linked item"} yet.</p>
              : (
                <div className="max-h-64 overflow-y-auto">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1">{files.linked.reference} · {files.linked.title}</p>
                  {files.files.map(f => {
                    const on = value.some(v => (v.file_id && v.file_id === f.file_id) || (v.url && v.url === f.url));
                    return (
                      <button key={f.file_id || f.url} type="button" onClick={() => { if (!on) add({ ...f, source: f.source }); }} className={`w-full text-left rounded-md px-2 py-1.5 flex items-center gap-2 ${on ? "opacity-60" : "hover:bg-muted"}`} data-testid={`reply-attach-linked-${f.file_id || "url"}`}>
                        <Icon type={f.type} />
                        <span className="min-w-0 flex-1"><span className="text-xs font-medium block truncate">{f.name}</span><span className="text-[10px] text-muted-foreground">{[f.source, fmtSize(f.size)].filter(Boolean).join(" · ")}</span></span>
                        {on && <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
          </PopoverContent>
        </Popover>
        {value.length > 0 && <span className="text-[11px] text-muted-foreground" data-testid="reply-attach-total">{value.length} file{value.length === 1 ? "" : "s"} · {fmtSize(total)} of 20 MB</span>}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="reply-attach-list">
          {value.map((a, i) => (
            <span key={`${a.file_id || a.url}-${i}`} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 pl-2 pr-1 py-1 text-xs" data-testid={`reply-attach-chip-${a.file_id || i}`}>
              <Paperclip size={12} className="text-muted-foreground" /><span className="max-w-[180px] truncate">{a.name}</span><span className="text-[10px] text-muted-foreground">{fmtSize(a.size)}</span>
              <button type="button" onClick={() => remove(i)} className="p-0.5 rounded hover:bg-muted" aria-label={`Remove ${a.name}`} data-testid={`reply-attach-remove-${a.file_id || i}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
